import React, { useEffect, useState } from "react";

import { localLargeStorage } from "../../../utils/PrivateUtils";
import { MD5 } from "crypto-js";
import { logDebug, logError } from "../../../shared/common/logger";

const ImageCropping: React.FC<{
	url: string;
	rect: {
		x?: number;
		y?: number;
		left?: number;
		top?: number;
		width: number;
		height: number;
	};
}> = ({ url, rect }) => {
	// Destructure the rect object to extract x, y, width, and height
	let { x, y, width, height, left, top } = rect;
	x = x || left || 0;
	y = y || top || 0;
	// logDebug('ImageCropping', { x, y, width, height, url });
	// Define inline styles for the cropping container
	const imageStyle = {
		width: `${width}px`, // Use the width from the rect
		height: `${height}px`, // Use the height from the rect
		backgroundImage: `url(${encodeURI(url)})`, // Set the image URL
		// backgroundSize: 'cover', // Adjust this value as needed to fit the image properly
		backgroundPosition: `-${x}px -${y}px`, // Shift the image based on x and y from the rect
		backgroundRepeat: "no-repeat",
	};

	return <div style={imageStyle}>s{/* The specified area of the image will be displayed here */}</div>;
};

const ImageCropping2: React.FC<{
	paddingPixels?: number;
	url: string;
	rect: {
		x?: number;
		y?: number;
		left?: number;
		top?: number;
		width: number;
		height: number;
	};
}> = ({ url, rect, paddingPixels }) => {
	// Destructure the rect object to extract x, y, width, and height
	let { x, y, width, height, left, top } = rect;
	x = x || left || 0;
	y = y || top || 0;
	paddingPixels = paddingPixels || 0;
	width = width + paddingPixels * 2;
	height = height + paddingPixels * 2;
	x = x - paddingPixels;
	y = y - paddingPixels;
	if (x < 0) x = 0;
	if (y < 0) y = 0;
	const [backgroundImage, setBackgroundImage] = useState<string>("");
	useEffect(() => {
		async function fetchImage() {
			const result = await localLargeStorage.getItem(MD5(url).toString());
			if (result) {
				setBackgroundImage(result);
				return;
			}
			try {
				const response = await fetch(url);
				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}
				const blob = await response.blob();
				const reader = new FileReader();
				reader.onloadend = () => {
					const result = reader.result as string;
					if (result.length > 100) {
						// TODO :: just a simple check to see if the image is loaded. do a better check
						localLargeStorage.setItem(MD5(url).toString(), result);
					}
					setBackgroundImage(reader.result as string);
				};
				reader.readAsDataURL(blob);
			} catch (error) {
				console.error("Error fetching image:", error);
			}
		}
		fetchImage();
	}, []);
	const imageStyle = {
		width: `${width}px`,
		height: `${height}px`,
		backgroundImage: `url(${backgroundImage})`,
		backgroundPosition: `-${x}px -${y}px`,
		backgroundRepeat: "no-repeat",
	};

	return <div style={imageStyle}>{/* Your content here */}</div>;
};

export class ImagesDatabase {
	private _clearImage(url: string) {
		logDebug("Clearing image from cache:", url);
		delete this._images[url];
		delete this._cacheTime[url];
	}
	private static instance: ImagesDatabase;
	private constructor() {
		// Private constructor to prevent instantiation
	}
	public static getInstance() {
		if (!ImagesDatabase.instance) {
			ImagesDatabase.instance = new ImagesDatabase();
		}
		return ImagesDatabase.instance;
	}

	private _lastScanTime: number = 0;
	private _images: { [key: string]: any } = {};
	private _cacheTime: { [key: string]: number } = {};

	async getImage(url: string): Promise<typeof Image> {
		const retImage = this._images[url];
		if (retImage) {
			this._cacheTime[url] = Date.now();
		}

		if (Date.now() - this._lastScanTime > 1000 * 60 * 15) {
			this._doScan();
		}

		if (retImage) {
			return retImage;
		}

		const that = this;
		return new Promise<any>((resolve, reject) => {
			const image = new Image();

			image.crossOrigin = "Anonymous";
			image.onload = () => {
				// logDebug("Image loaded to cache:", url);
				that._images[url] = image;
				that._cacheTime[url] = Date.now();
				resolve(image);
			};
			image.onerror = (err) => {
				reject(err);
			};
			image.src = url;
		});
	}
	private _doScan() {
		const now = Date.now();
		for (const key in this._cacheTime) {
			if (now - (this._cacheTime[key] || 0) > 1000 * 60 * 30) {
				this._clearImage(key);
			}
		}
		this._lastScanTime = now;
	}
}

const ImageCanvas: React.FC<{
	className?: string;
	paddingPixels?: number;
	url: string;
	rect: {
		x?: number;
		y?: number;
		left?: number;
		top?: number;
		width: number;
		height: number;
	};
}> = ({ className, url, rect, paddingPixels }) => {
	// Destructure the rect object to extract x, y, width, and height
	// logDebug('ImageCanvas', { url, rect });
	rect = rect || { x: 0, y: 0, width: 0, height: 0 };
	let { x, y, width, height, left, top } = rect;
	x = x || left || 0;
	y = y || top || 0;
	paddingPixels = paddingPixels || 0;
	width = width + paddingPixels * 2;
	height = height + paddingPixels * 2;
	x = x - paddingPixels;
	y = y - paddingPixels;
	if (x < 0) x = 0;
	if (y < 0) y = 0;

	const [croppedImageUrl, setCroppedImageUrl] = useState("");

	async function setCanves() {
		try {
			const image = await ImagesDatabase.getInstance().getImage(url);
			if (rect.width === 0 && rect.height === 0) {
				//@ts-ignore
				width = image.width;
				//@ts-ignore
				height = image.height;
			}

			const canvas = document.createElement("canvas");
			canvas.width = width;
			canvas.height = height;

			const ctx: any = canvas.getContext("2d");

			// Draw the desired region of the image onto the canvas
			ctx.drawImage(
				image,
				x,
				y,
				width,
				height, // Source rectangle
				0,
				0,
				width,
				height, // Destination rectangle
			);

			// Convert the canvas to a data URL
			const dataUrl: any = canvas.toDataURL();

			// Set the data URL to the state
			setCroppedImageUrl(dataUrl);
		} catch (err) {
			logError("Error loading image:", err);
		}
	}
	useEffect(() => {
		setCanves();
	}, [url, x, y, width, height]);

	if (!croppedImageUrl) {
		return null;
	}
	return <img src={croppedImageUrl} alt="Cropped Image" className={className ?? ""} />;
};
export { ImageCropping2, ImageCanvas };
export default ImageCropping;
